import { useState, useEffect, useCallback } from 'react';
import { GOOGLE_CLIENT_ID, DRIVE_SCOPE } from '../config.jsx';
import { DriveAPI } from '../core/DriveAPI.js';

export function useAuth() {
    const [authUser, setAuthUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [authRestoring, setAuthRestoring] = useState(false);

    // ── Auth boot: restore identity from localStorage ──────    
    useEffect(() => {
        const stored = localStorage.getItem('iPlanner_user');
        if (!stored) {
            setAuthChecked(true);
            return;
        }
        let parsedUser;
        try { parsedUser = JSON.parse(stored); } catch (_) {
            localStorage.removeItem('iPlanner_user');
            setAuthChecked(true);
            return;
        }
        // Restore identity immediately -- no splash, no GIS round-trip
        console.log('[iPlanner] Identity restored:', parsedUser.email);
        setAuthUser(parsedUser);
        setAuthChecked(true);
    }, []);

    const handleSignedIn = useCallback((user, token) => {
        window.__gAccessToken = token;
        setAuthUser(user);
    }, []);

    const handleSignOut = useCallback(() => {
        // Revoke before clearing so we pass the real token
        const tokenToRevoke = window.__gAccessToken;
        localStorage.removeItem('iPlanner_user');
        window.__gAccessToken = null;
        window.__gTokenExpiry = null;
        DriveAPI.clearPersistedIds();
        setAuthUser(null);
        if (window.google && window.google.accounts && tokenToRevoke) {
            window.google.accounts.oauth2.revoke(tokenToRevoke, () => {
                console.log('[iPlanner] Token revoked');
            });
        }
    }, []);

    // ── Lazy Drive token acquisition ─────────────────────────
    // Returns a valid Drive token, reusing an existing one or requesting
    // a fresh one via GIS. 
    const acquireDriveToken = useCallback((forceInteractive = false) => new Promise((resolve, reject) => {
        // Reuse existing token if still valid (30s safety buffer)
        if (!forceInteractive && window.__gAccessToken && window.__gTokenExpiry &&
            Date.now() < window.__gTokenExpiry - 30000) {
            resolve(window.__gAccessToken);
            return;
        }
        if (!window.google || !window.google.accounts) {
            reject(new Error('GIS not loaded'));
            return;
        }
        const storedUser = localStorage.getItem('iPlanner_user');
        if (!storedUser) { reject(new Error('No stored user')); return; }
        const user = JSON.parse(storedUser);

        let tokenRequestTimeout = null;
        let requestCompleted = false;

        if (!forceInteractive && isPWAStandalone()) {
            tokenRequestTimeout = setTimeout(() => {
                if (!requestCompleted) {
                    console.warn('[iPlanner] Silent token request timed out (iOS PWA). Forcing interactive login.');
                    reject(new Error('timeout'));
                }
            }, 8000);
        }

        const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: DRIVE_SCOPE + ' email profile',
            hint: user.email,
            prompt: forceInteractive ? 'consent' : '', // Force interaction if requested
            callback: (tokenResponse) => {
                requestCompleted = true;
                if (tokenRequestTimeout) clearTimeout(tokenRequestTimeout);

                if (tokenResponse.error || !tokenResponse.access_token) {
                    reject(new Error(tokenResponse.error || 'no token'));
                    return;
                }
                window.__gAccessToken = tokenResponse.access_token;
                window.__gTokenExpiry = Date.now() + (tokenResponse.expires_in * 1000);
                console.log('[iPlanner] Drive token refreshed');
                resolve(window.__gAccessToken);
            },
        });
        client.requestAccessToken();
    }), []);

    // Add INSIDE useAuth() function, before acquireDriveToken:
    const isPWAStandalone = useCallback(() => {
        return window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator.standalone === true) ||
            document.referrer.includes('android-app://');
    }, []);

    return {
        authUser,
        authChecked,
        authRestoring,
        handleSignedIn,
        handleSignOut,
        acquireDriveToken
    };
}